import React, { useState, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, query, where, getDocs, deleteDoc, doc, serverTimestamp, orderBy } from 'firebase/firestore';
import { storage, db, auth } from '../services/firebase';
import LoadingSpinner from '../components/LoadingSpinner';

const SuperAdminMarketingPackage = () => {
    const [photos, setPhotos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [adBanners, setAdBanners] = useState([]);

    // We don't need subscription limits for Super Admin
    // const [limits, setLimits] = useState({ max: 0, used: 0, remaining: 0 });

    const [targetLink, setTargetLink] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch All Active Banners for Super Admin to managing or just their own?
            // "Marketing" page usually showed "My Campaigns". 
            // If we are shifting "Marketing Image Upload" to Super Admin, 
            // let's show the banners uploaded by Super Admin.
            // But if Super Admin wants to see ALL banners, they use SuperAdminAds.jsx.
            // So here we likely just show what THEY uploaded or maybe all?
            // Let's stick to what Marketing.jsx did: "My Campaigns".
            // So we fetch ads where userId == auth.currentUser.uid

            const user = auth.currentUser;
            if (user) {
                // Removed orderBy to avoid index requirement errors
                const q = query(
                    collection(db, 'ad_banners'),
                    where('userId', '==', user.uid)
                );

                const querySnapshot = await getDocs(q);
                const banners = [];
                querySnapshot.forEach((doc) => {
                    banners.push({ id: doc.id, ...doc.data() });
                });

                // Client-side sort
                banners.sort((a, b) => {
                    const dateA = a.createdAt?.seconds || 0;
                    const dateB = b.createdAt?.seconds || 0;
                    return dateB - dateA;
                });

                setAdBanners(banners);
            }
        } catch (error) {
            console.error('Error fetching marketing data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePhotoChange = (e) => {
        const files = Array.from(e.target.files);
        setPhotos(prev => [...prev, ...files]);
    };

    const removePhoto = (index) => {
        setPhotos(prev => prev.filter((_, i) => i !== index));
    };

    const handleDeleteBanner = async (bannerId) => {
        if (!window.confirm('Are you sure you want to delete this ad banner?')) return;

        try {
            await deleteDoc(doc(db, 'ad_banners', bannerId));
            fetchData();
            alert('Banner deleted successfully');
        } catch (error) {
            console.error('Error deleting banner:', error);
            alert('Failed to delete banner');
        }
    };

    const handleUpload = async () => {
        if (photos.length === 0) {
            alert('Please select at least one photo');
            return;
        }

        setUploading(true);
        const user = auth.currentUser;

        try {
            let successCount = 0;

            for (const photo of photos) {
                // 1. Upload to Storage
                // Use a generic path or specific? Marketing.jsx used ad-banners/{uid}/...
                const storageRef = ref(storage, `ad-banners/${user.uid}/${Date.now()}_${photo.name}`);
                await uploadBytes(storageRef, photo);
                const url = await getDownloadURL(storageRef);

                // 2. Save to Firestore
                await addDoc(collection(db, 'ad_banners'), {
                    userId: user.uid,
                    imageUrl: url,
                    link: targetLink, // Save the link
                    fileName: photo.name,
                    createdAt: serverTimestamp(),
                    status: 'active',
                    userEmail: user.email,
                    userName: 'Super Admin', // Hardcoded or fetch profile
                    userType: 'superadmin' // Tag as superadmin
                });

                successCount++;
            }

            alert(`${successCount} ad banners published successfully!`);
            setPhotos([]);
            setTargetLink(''); // Reset link
            fetchData(); // Refresh list

        } catch (error) {
            console.error('Error uploading photos:', error);
            alert('Error uploading photos. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="min-h-screen bg-slate-50/50 relative overflow-hidden pb-24">
            {/* Background grid pattern */}
            <div className="absolute inset-0 -z-10 bg-[radial-gradient(#0e3a61_1px,transparent_1px)] [background-size:24px_24px] opacity-5"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">

                {/* Header Banner */}
                <div className="bg-gradient-to-br from-[#0e3a61] to-[#051728] rounded-[2rem] rounded-tr-[4rem] rounded-bl-[4rem] shadow-xl border border-[#0e3a61]/10 overflow-hidden relative p-8 mb-8">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-20 translate-x-20 pointer-events-none"></div>
                    <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-[#e2822f]/10 rounded-full blur-2xl pointer-events-none"></div>
                    
                    <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-6 z-10 text-white">
                        <div className="flex-1 min-w-0">
                            <span className="text-[10px] uppercase font-bold tracking-widest text-[#e2822f] bg-[#e2822f]/15 px-3 py-1 rounded-full border border-[#e2822f]/20">
                                Portal Marketing
                            </span>
                            <h1 className="text-2xl sm:text-3xl font-extrabold mt-3 tracking-tight">
                                Ad Campaigns Manager
                            </h1>
                            <p className="mt-2 text-slate-350 text-xs sm:text-sm max-w-xl leading-relaxed">
                                Publish, audit, and regulate target banner ads across the portal.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Deploy Promotional Banner Config Section */}
                <div className="bg-white rounded-[2rem] shadow-xl p-8 mb-8 border border-slate-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#0e3a61]/5 to-[#508d0c]/5 rounded-full -translate-y-16 translate-x-16 pointer-events-none"></div>
                    <h2 className="text-lg font-black text-slate-800 mb-6 flex items-center gap-2">
                        <span className="w-1.5 h-6 bg-[#0e3a61] rounded-full"></span>
                        Deploy Promotional Banner
                    </h2>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Dropzone and Link Input */}
                        <div className="space-y-6">
                            <div className="border-2 border-dashed border-slate-200 hover:border-[#0e3a61]/50 hover:bg-[#0e3a61]/5 rounded-2xl p-8 text-center transition-all duration-300 cursor-pointer relative group">
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={handlePhotoChange}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
                                    disabled={uploading}
                                />
                                <div className="space-y-4">
                                    <div className="w-14 h-14 bg-slate-50 text-slate-400 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-105 group-hover:bg-[#0e3a61] group-hover:text-white transition-all duration-300 border border-slate-100 shadow-sm">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-sm font-extrabold text-slate-800">Drag & drop image files here, or click to browse</p>
                                        <p className="text-xs text-slate-400 font-semibold mt-1">Recommended resolution: 1200x400. Supports PNG, JPG, GIF</p>
                                    </div>
                                </div>
                            </div>

                            {/* Redirect Link Input */}
                            <div>
                                <label className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-2">
                                    Target Destination Link (Optional)
                                </label>
                                <input
                                    type="url"
                                    value={targetLink}
                                    onChange={(e) => setTargetLink(e.target.value)}
                                    placeholder="https://example.com/target-landing-page"
                                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-semibold text-slate-700 bg-slate-50/50"
                                />
                                <p className="text-[10px] text-slate-450 font-semibold mt-1.5 leading-relaxed">
                                    Specifies the target landing page when an employer or seeker clicks this ad.
                                </p>
                            </div>
                        </div>

                        {/* Preview and Upload Action List */}
                        <div className="flex flex-col justify-between space-y-6">
                            <div className="space-y-3.5">
                                <h3 className="text-xs font-extrabold text-slate-500 uppercase tracking-widest">Ready for Upload ({photos.length})</h3>
                                {photos.length === 0 ? (
                                    <div className="text-slate-400 italic text-xs font-semibold py-8 text-center bg-slate-50 rounded-2xl border border-slate-100">
                                        No images staged for upload...
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[160px] overflow-y-auto p-1 bg-slate-50/50 rounded-2xl border border-slate-100">
                                        {photos.map((photo, index) => (
                                            <div key={index} className="relative group aspect-[3/1] bg-slate-100 rounded-xl overflow-hidden border border-slate-200">
                                                <img
                                                    src={URL.createObjectURL(photo)}
                                                    alt="staged-ad-preview"
                                                    className="w-full h-full object-cover"
                                                />
                                                <button
                                                    onClick={() => removePhoto(index)}
                                                    className="absolute top-1 right-1 bg-red-650 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 hover:bg-red-700 transition-opacity"
                                                    title="Cancel upload"
                                                >
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="pt-4 border-t border-slate-100">
                                <button
                                    onClick={handleUpload}
                                    disabled={uploading || photos.length === 0}
                                    className="w-full py-3 bg-gradient-to-r from-[#0e3a61] to-[#09253f] hover:from-[#09253f] hover:to-[#051728] text-white rounded-xl font-extrabold text-xs uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center shadow-md hover:shadow-lg transition-all duration-200"
                                >
                                    {uploading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Deploying Banners...
                                        </>
                                    ) : `Deploy & Run ${photos.length} Campaign Banner(s)`}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Promo campaigns gallery section */}
                <div className="space-y-6">
                    <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">
                        <span className="w-1.5 h-6 bg-[#508d0c] rounded-full"></span>
                        Internal Promo Campaigns ({adBanners.length})
                    </h2>
                    
                    {adBanners.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-[2rem] shadow-xl border border-slate-100">
                            <div className="w-20 h-20 bg-slate-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-slate-100 border-dashed">
                                <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-base font-bold text-slate-900 mb-2">No internal marketing campaigns found</h3>
                            <p className="text-xs text-slate-500 max-w-sm mx-auto">Upload new ad banners above to run campaign banners across seeker and employer routing zones.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {adBanners.map((banner) => (
                                <div key={banner.id} className="bg-white rounded-[2rem] shadow-md overflow-hidden border border-slate-150 flex flex-col justify-between group hover:shadow-2xl hover:border-[#0e3a61]/25 hover:-translate-y-1 transition-all duration-300 relative">
                                    
                                    {/* Aspect Video image block */}
                                    <div className="relative aspect-[3/1.1] bg-slate-100 border-b border-slate-150 overflow-hidden">
                                        <img
                                            src={banner.imageUrl}
                                            alt="Marketing Campaign Banner"
                                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                                        />
                                        <div className="absolute inset-0 bg-slate-900/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                                        
                                        {/* Status badge */}
                                        <div className="absolute top-3 left-3 z-10">
                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider shadow-sm text-white ${
                                                banner.status === 'active' ? 'bg-[#508d0c]' : 'bg-slate-500'
                                            }`}>
                                                <span className={`w-1.5 h-1.5 rounded-full bg-white ${banner.status === 'active' ? 'animate-ping' : ''}`}></span>
                                                {banner.status === 'active' ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>

                                        {/* Hover revealed Delete Button */}
                                        <button
                                            onClick={() => handleDeleteBanner(banner.id)}
                                            className="absolute top-3 right-3 bg-white hover:bg-red-50 text-red-650 hover:text-red-700 p-2.5 rounded-xl shadow-lg border border-red-100 opacity-0 group-hover:opacity-100 transition-all duration-200"
                                            title="Delete Banner"
                                        >
                                            <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>

                                    {/* Campaign details */}
                                    <div className="p-5 flex-1 flex flex-col justify-between gap-3 text-xs font-semibold">
                                        <div className="space-y-1">
                                            <p className="font-extrabold text-slate-800 text-sm truncate" title={banner.fileName}>
                                                {banner.fileName || 'Campaign Banner Asset'}
                                            </p>
                                            {banner.link ? (
                                                <p className="text-[11px] text-slate-500 truncate">
                                                    🔗 Target: <a href={banner.link} target="_blank" rel="noopener noreferrer" className="hover:underline hover:text-[#0e3a61] text-[#0e3a61]/80 font-bold">{banner.link}</a>
                                                </p>
                                            ) : (
                                                <p className="text-[11px] text-slate-400 italic">No destination link configured</p>
                                            )}
                                        </div>

                                        <div className="flex items-center justify-between text-[10px] text-slate-400 font-bold uppercase tracking-wider pt-3 border-t border-slate-100">
                                            <span>Format: JPG/PNG</span>
                                            <span>
                                                {banner.createdAt ? new Date(banner.createdAt.seconds * 1000).toLocaleDateString() : 'Just now'}
                                            </span>
                                        </div>
                                    </div>

                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default SuperAdminMarketingPackage;
